import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Select,
  Button,
  Card,
  Row,
  Col,
  Checkbox,
  Space,
  Typography,
  Divider,
  message,
  Spin,
  Collapse,
  Tag,
} from 'antd';
import {
  SaveOutlined,
  ArrowLeftOutlined,
  PlusOutlined,
  FileTextOutlined,
  LinkOutlined,
  AppstoreOutlined,
  GlobalOutlined,
  ReadOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { menuService } from '../../services/menu.service';
import { apiClient } from '../../services/api';
import { useMenuDetail } from '../../hooks/useMenus';
import { MenuTreeEditor } from './MenuTreeEditor';
import type { MenuItem } from '../../services/menu.service';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { Panel } = Collapse;

const MENU_POSITION_OPTIONS = [
  { value: 'main', label: 'Menu chính' },
  { value: 'footer', label: 'Footer' },
  { value: 'sidebar', label: 'Sidebar' },
  { value: 'header', label: 'Header' },
  { value: 'mobile', label: 'Mobile' },
  { value: 'top', label: 'Top' },
];

const COLLECTION_OPTIONS = [
  { value: '', label: 'Tất cả' },
  { value: 'vang', label: 'Vàng' },
  { value: 'bac', label: 'Bạc' },
];

function genId(): string {
  return `item_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

export const MenuEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id && id !== 'add';
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);

  // Inline add states
  const [customTitle, setCustomTitle] = useState('');
  const [customLink, setCustomLink] = useState('');
  const [customBlank, setCustomBlank] = useState(false);
  const [textTitle, setTextTitle] = useState('');

  // Pages
  const [pages, setPages] = useState<{ _id: string; title: string; slug: string }[]>([]);
  const [pagesQuery, setPagesQuery] = useState('');
  const [pagesLoading, setPagesLoading] = useState(false);
  const [selectedPages, setSelectedPages] = useState<Set<string>>(new Set());

  // Articles
  const [articles, setArticles] = useState<{ _id: string; title: string; slug: string }[]>([]);
  const [articlesQuery, setArticlesQuery] = useState('');
  const [articlesLoading, setArticlesLoading] = useState(false);
  const [selectedArticles, setSelectedArticles] = useState<Set<string>>(new Set());

  const { menu, loading } = useMenuDetail(isEdit ? id : undefined);

  useEffect(() => {
    if (menu) {
      form.setFieldsValue({
        title: menu.title,
        description: menu.description,
        slug: menu.slug,
        state: menu.state?.toString() ?? '1',
        position: menu.position ?? ['main'],
        ordering: menu.ordering ?? 0,
        collection: menu.collection_slug ?? '',
      });
      setMenuItems(
        (menu.items || []).map((item, idx) => ({
          ...item,
          ordering: item.ordering ?? idx,
        })),
      );
    }
  }, [menu, form]);

  const getMaxRootOrdering = () => {
    const roots = menuItems.filter((i) => !i.parent_id);
    return roots.length > 0 ? Math.max(...roots.map((i) => i.ordering)) : -1;
  };

  const addCustomLink = () => {
    if (!customTitle.trim() || !customLink.trim()) {
      message.warning('Vui lòng nhập tiêu đề và link');
      return;
    }
    setMenuItems([
      ...menuItems,
      {
        id: genId(),
        title: customTitle,
        link: customLink,
        target: customBlank ? '_blank' : '_self',
        ordering: getMaxRootOrdering() + 1,
        item_type: 'custom',
      },
    ]);
    setCustomTitle('');
    setCustomLink('');
    setCustomBlank(false);
  };

  const addTextItem = () => {
    if (!textTitle.trim()) {
      message.warning('Vui lòng nhập tiêu đề');
      return;
    }
    setMenuItems([
      ...menuItems,
      {
        id: genId(),
        title: textTitle,
        link: '',
        target: '_self',
        ordering: getMaxRootOrdering() + 1,
        item_type: 'text',
      },
    ]);
    setTextTitle('');
  };

  const searchPages = async (q: string) => {
    setPagesLoading(true);
    try {
      const res = await apiClient.get('/pages', { params: { q, limit: 15 } });
      const raw = res.data?.data ?? res.data ?? [];
      setPages(Array.isArray(raw) ? raw : raw.data ?? []);
    } catch {
      setPages([]);
    } finally {
      setPagesLoading(false);
    }
  };

  const addSelectedPages = () => {
    const selected = pages.filter((p) => selectedPages.has(p._id));
    if (!selected.length) return;
    const maxOrd = getMaxRootOrdering();
    setMenuItems([
      ...menuItems,
      ...selected.map((p, i) => ({
        id: genId(),
        title: p.title,
        link: `/${p.slug}`,
        target: '_self' as const,
        ordering: maxOrd + 1 + i,
        item_type: 'page' as const,
        item_id: p._id,
      })),
    ]);
    setSelectedPages(new Set());
    message.success(`Đã thêm ${selected.length} trang vào menu`);
  };

  const searchArticles = async (q: string) => {
    setArticlesLoading(true);
    try {
      const res = await apiClient.get('/articles', { params: { q, limit: 15 } });
      const raw = res.data?.data ?? res.data ?? [];
      setArticles(Array.isArray(raw) ? raw : raw.data ?? []);
    } catch {
      setArticles([]);
    } finally {
      setArticlesLoading(false);
    }
  };

  const addSelectedArticles = () => {
    const selected = articles.filter((a) => selectedArticles.has(a._id));
    if (!selected.length) return;
    const maxOrd = getMaxRootOrdering();
    setMenuItems([
      ...menuItems,
      ...selected.map((a, i) => ({
        id: genId(),
        title: a.title,
        link: `/${a.slug}`,
        target: '_self' as const,
        ordering: maxOrd + 1 + i,
        item_type: 'article' as const,
        item_id: a._id,
      })),
    ]);
    setSelectedArticles(new Set());
    message.success(`Đã thêm ${selected.length} bài viết vào menu`);
  };

  const onFinish = async (values: any) => {
    setSaving(true);
    try {
      const payload = {
        title: values.title,
        description: values.description || null,
        slug: values.slug || undefined,
        state: Number(values.state),
        position: values.position || ['main'],
        ordering: Number(values.ordering) || 0,
        items: menuItems,
        collection: values.collection || null,
      };

      if (isEdit) {
        await menuService.updateMenu(id!, payload);
        message.success('Cập nhật menu thành công');
      } else {
        await menuService.createMenu(payload);
        message.success('Tạo menu thành công');
      }
      navigate('/menu');
    } catch (err: any) {
      const errData = err?.response?.data;
      if (errData?.message) {
        message.error(errData.message);
      } else if (errData?.errors) {
        const firstErr = Object.values(errData.errors)[0];
        message.error(firstErr as string);
      } else {
        message.error('Có lỗi xảy ra, vui lòng thử lại');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>
            {isEdit ? 'Chỉnh sửa menu' : 'Thêm menu mới'}
          </Title>
          <Text type="secondary">
            {isEdit ? 'Cập nhật thông tin và cấu trúc menu' : 'Tạo menu điều hướng mới'}
          </Text>
        </div>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/menu')}>
          Quay lại
        </Button>
      </div>

      <Form form={form} layout="vertical" onFinish={onFinish} initialValues={{ state: '1', position: ['main'], ordering: 0, collection: '' }}>
        <Row gutter={16}>
          {/* Left: Add items */}
          <Col xs={24} lg={6}>
            <Card title="Thêm từ nội dung" size="small">
              <Collapse accordion size="small" ghost>
                <Panel
                  key="text"
                  header={
                    <Space>
                      <FileTextOutlined />
                      <span>Text Menu</span>
                    </Space>
                  }
                >
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Input
                      placeholder="Tiêu đề"
                      value={textTitle}
                      onChange={(e) => setTextTitle(e.target.value)}
                      onPressEnter={addTextItem}
                    />
                    <Button type="primary" block icon={<PlusOutlined />} onClick={addTextItem}>
                      Thêm
                    </Button>
                  </Space>
                </Panel>

                <Panel
                  key="custom"
                  header={
                    <Space>
                      <LinkOutlined />
                      <span>Custom Link</span>
                    </Space>
                  }
                >
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Input
                      placeholder="Tiêu đề *"
                      value={customTitle}
                      onChange={(e) => setCustomTitle(e.target.value)}
                    />
                    <Input
                      placeholder="Link *"
                      value={customLink}
                      onChange={(e) => setCustomLink(e.target.value)}
                    />
                    <Checkbox
                      checked={customBlank}
                      onChange={(e) => setCustomBlank(e.target.checked)}
                    >
                      Mở link ngoài (_blank)
                    </Checkbox>
                    <Button type="primary" block icon={<PlusOutlined />} onClick={addCustomLink}>
                      Thêm
                    </Button>
                  </Space>
                </Panel>

                <Panel
                  key="pages"
                  header={
                    <Space>
                      <GlobalOutlined />
                      <span>Trang</span>
                    </Space>
                  }
                >
                  <Space style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Input.Search
                      placeholder="Tìm kiếm trang..."
                      loading={pagesLoading}
                      value={pagesQuery}
                      onChange={(e) => setPagesQuery(e.target.value)}
                      onSearch={searchPages}
                      allowClear
                      size="small"
                    />
                    {pages.length > 0 && (
                      <>
                        <div style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: 4 }}>
                          {pages.map((p) => (
                            <div
                              key={p._id}
                              style={{ padding: '4px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
                              onClick={() => {
                                const next = new Set(selectedPages);
                                next.has(p._id) ? next.delete(p._id) : next.add(p._id);
                                setSelectedPages(next);
                              }}
                            >
                              <Checkbox checked={selectedPages.has(p._id)} />
                              <span style={{ fontSize: 12 }}>{p.title}</span>
                            </div>
                          ))}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Checkbox
                            checked={selectedPages.size === pages.length && pages.length > 0}
                            onChange={(e) =>
                              setSelectedPages(e.target.checked ? new Set(pages.map((p) => p._id)) : new Set())
                            }
                          >
                            <span style={{ fontSize: 12 }}>Chọn tất cả</span>
                          </Checkbox>
                          {selectedPages.size > 0 && (
                            <Button size="small" type="primary" icon={<PlusOutlined />} onClick={addSelectedPages}>
                              Thêm ({selectedPages.size})
                            </Button>
                          )}
                        </div>
                      </>
                    )}
                  </Space>
                </Panel>

                <Panel
                  key="articles"
                  header={
                    <Space>
                      <ReadOutlined />
                      <span>Bài viết</span>
                    </Space>
                  }
                >
                  <Space style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Input.Search
                      placeholder="Tìm kiếm bài viết..."
                      loading={articlesLoading}
                      value={articlesQuery}
                      onChange={(e) => setArticlesQuery(e.target.value)}
                      onSearch={searchArticles}
                      allowClear
                      size="small"
                    />
                    {articles.length > 0 && (
                      <>
                        <div style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: 4 }}>
                          {articles.map((a) => (
                            <div
                              key={a._id}
                              style={{ padding: '4px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
                              onClick={() => {
                                const next = new Set(selectedArticles);
                                next.has(a._id) ? next.delete(a._id) : next.add(a._id);
                                setSelectedArticles(next);
                              }}
                            >
                              <Checkbox checked={selectedArticles.has(a._id)} />
                              <span style={{ fontSize: 12 }}>{a.title}</span>
                            </div>
                          ))}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Checkbox
                            checked={selectedArticles.size === articles.length && articles.length > 0}
                            onChange={(e) =>
                              setSelectedArticles(e.target.checked ? new Set(articles.map((a) => a._id)) : new Set())
                            }
                          >
                            <span style={{ fontSize: 12 }}>Chọn tất cả</span>
                          </Checkbox>
                          {selectedArticles.size > 0 && (
                            <Button size="small" type="primary" icon={<PlusOutlined />} onClick={addSelectedArticles}>
                              Thêm ({selectedArticles.size})
                            </Button>
                          )}
                        </div>
                      </>
                    )}
                  </Space>
                </Panel>
              </Collapse>
            </Card>
          </Col>

          {/* Center: Menu tree */}
          <Col xs={24} lg={12}>
            <Card
              title={
                <Space>
                  <AppstoreOutlined />
                  <span>Danh sách menu items</span>
                  <Tag color="blue">{menuItems.length} items</Tag>
                </Space>
              }
              size="small"
            >
              <MenuTreeEditor menuItems={menuItems} onChange={setMenuItems} />
            </Card>
          </Col>

          {/* Right: Menu info */}
          <Col xs={24} lg={6}>
            <Card title="Thông tin menu" size="small">
              <Form.Item
                label="Tên menu"
                name="title"
                rules={[
                  { required: true, message: 'Vui lòng nhập tên menu' },
                  { max: 200, message: 'Tối đa 200 ký tự' },
                ]}
              >
                <Input placeholder="VD: Menu chính" maxLength={200} showCount />
              </Form.Item>

              <Form.Item label="Mô tả" name="description">
                <TextArea rows={3} placeholder="Mô tả menu (không bắt buộc)" maxLength={500} showCount />
              </Form.Item>

              <Form.Item label="Slug" name="slug">
                <Input placeholder="VD: menu-chinh (tự động tạo nếu để trống)" />
              </Form.Item>

              <Form.Item label="Vị trí" name="position">
                <Checkbox.Group options={MENU_POSITION_OPTIONS} style={{ display: 'flex', flexDirection: 'column', gap: 4 }} />
              </Form.Item>

              <Form.Item label="Collection" name="collection">
                <Select>
                  {COLLECTION_OPTIONS.map((opt) => (
                    <Option key={opt.value || 'all'} value={opt.value}>
                      {opt.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item label="Thứ tự" name="ordering">
                <Input type="number" min={0} />
              </Form.Item>

              <Form.Item label="Trạng thái" name="state">
                <Select>
                  <Option value="1">Hoạt động</Option>
                  <Option value="0">Tắt</Option>
                </Select>
              </Form.Item>

              <Divider />

              <Button
                type="primary"
                htmlType="submit"
                block
                icon={<SaveOutlined />}
                loading={saving}
              >
                {saving ? 'Đang lưu...' : 'Lưu menu'}
              </Button>
            </Card>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default MenuEdit;
